'use client';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import useCheckBreakpointActive from '@/hooks/useBreakpoint';
import React, { useEffect } from 'react';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';
import { useSpringCarousel } from 'react-spring-carousel';

const getImpactIcon = (impact) => {
  if (!impact.icon && impact.id) {
    switch (impact.id) {
      case 'hours-volunteered':
        return '/assets/icons/hours-volunteered.svg';
      case 'donations-made':
        return '/assets/icons/donations-made.svg';
      case 'lives-impacted':
        return '/assets/icons/lives-impacted.svg';
      case 'in-kind-donations-made':
        return '/assets/icons/inkind-campaign.svg';
      case 'in-kind-donations-received':
        return '/assets/icons/fundraise-campaign.svg';
      case 'volunteering-opportunities':
        return '/assets/icons/volunteer.svg';
      case 'total-supporters':
        return '/assets/icons/total-supporters.svg';
      default:
        return '/assets/icons/heart.svg';
    }
  } else if (!impact.icon) {
    return '/assets/icons/heart.svg';
  }

  return `/assets/icons/${impact.icon}`;
};

const getImpactTitle = (impact) => {
  if (!impact.label && impact.id) {
    switch (impact.id) {
      case 'hours-volunteered':
        return 'Hours Volunteered';
      case 'donations-made':
        return 'Donations Made';
      case 'lives-impacted':
        return 'Lives Impacted';
      case 'in-kind-donations-made':
        return 'In-Kind Donations Made';
      case 'in-kind-donations-received':
        return 'In-Kind Donations Received';
      case 'volunteering-opportunities':
        return 'Volunteering Opportunities';
      case 'total-supporters':
        return 'Total Supporters';
    }
  }
  return impact.label || '';
};

const formatImpactValue = (value) => {
  if (value.toLocaleString) {
    return value.toLocaleString({
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }
  return value;
};

function SocialImpactCarousel(props) {
  const { impacts } = props;

  const isSmallScreen = useCheckBreakpointActive('sm');
  const itemsPerSlide = isSmallScreen ? 1 : 4;

  const carouselItems = [...impacts];
  if (carouselItems.length < itemsPerSlide) {
    const emptyItems = Array(itemsPerSlide - carouselItems.length)
      .fill()
      .map((i, index) => ({ id: `empty-${index}`, empty: true }));
    carouselItems.push(...emptyItems);
  }

  const renderItem = (impact) => {
    if (impact.empty) {
      return <div key={impact.id} className="w-full h-full" />;
    }
    return (
      <div className="flex flex-col items-center gap-2 w-full">
        <img
          src={getImpactIcon(impact)}
          alt={getImpactTitle(impact)}
          className="object-contain w-14 h-14"
        />
        <Typography variant="h1" component="h2">{formatImpactValue(impact.value)}</Typography>
        <Typography variant="body1" className="text-center">
          {getImpactTitle(impact)}
        </Typography>
      </div>
    );
  };

  const canNavigate = impacts.length > itemsPerSlide;

  const {
    carouselFragment,
    slideToPrevItem,
    slideToNextItem,
    getCurrentActiveItem,
  } = useSpringCarousel({
    slideAmount: 200,
    withLoop: canNavigate,
    itemsPerSlide: itemsPerSlide,
    gutter: isSmallScreen ? 0 : 40,
    items: carouselItems.map((impact) => ({
      id: impact.id,
      renderItem: renderItem(impact),
    })),
  });

  const handleNextClick = () => {
    slideToNextItem();
  };

  const handlePrevClick = () => {
    slideToPrevItem();
  };

  return (
    <div className="flex items-center gap-2 lg:gap-11 justify-center mr-2 lg:mr-0">
      <Button variant="icon" onClick={handlePrevClick} disabled={!canNavigate}>
        <MdOutlineChevronLeft
          className={`w-8 h-8 ${
            canNavigate ? 'text-text-disabled' : 'text-body-background'
          }`}
        />
      </Button>
      <div className="flex-1 overflow-hidden">{carouselFragment}</div>
      <Button variant="icon" onClick={handleNextClick} disabled={!canNavigate}>
        <MdOutlineChevronRight
          className={`w-8 h-8 ${
            canNavigate ? 'text-text-disabled' : 'text-body-background'
          }`}
        />
      </Button>
    </div>
  );
}

export default SocialImpactCarousel;
